import { useState } from "react";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { ContactDialog } from "@/components/support/ContactDialog";
import { Plus, PencilSimple, UserMinus, ArrowCounterClockwise, Star, EnvelopeSimple, Phone } from "@phosphor-icons/react";
import { toast } from "sonner";

const ContactRow = ({ ct, onEdit, onDeactivate, onReactivate }) => {
  const inactive = ct.active === false;
  return (
    <div data-testid={`contact-row-${ct.contact_id}`} className={`flex items-start gap-3 py-3 ${inactive ? "opacity-50" : ""}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid={`contact-name-${ct.contact_id}`} onClick={() => onEdit(ct)} className="text-sm font-medium text-zinc-100 hover:text-blue-400 transition-colors text-left">{ct.name}</button>
          {ct.is_primary && <span data-testid={`contact-primary-badge-${ct.contact_id}`} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border border-blue-500/30 bg-blue-500/10 text-blue-300"><Star size={10} weight="fill" /> primary</span>}
          {inactive && <span className="text-[10px] px-1.5 py-0.5 rounded border border-white/10 text-zinc-500">inactive</span>}
          {ct.title && <span className="text-xs text-zinc-500">{ct.title}</span>}
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-0.5 text-xs text-zinc-400">
          {ct.email && <a href={`mailto:${ct.email}`} className="inline-flex items-center gap-1 hover:text-white break-all"><EnvelopeSimple size={12} />{ct.email}</a>}
          {ct.phone && <a href={`tel:${ct.phone}`} className="inline-flex items-center gap-1 hover:text-white"><Phone size={12} />{ct.phone}</a>}
        </div>
        {ct.notes && <div className="text-xs text-zinc-500 mt-1 whitespace-pre-wrap">{ct.notes}</div>}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <button data-testid={`contact-edit-${ct.contact_id}`} onClick={() => onEdit(ct)} title="Edit" className="p-1.5 rounded hover:bg-white/10 text-zinc-400 hover:text-white"><PencilSimple size={14} /></button>
        {inactive
          ? <button data-testid={`contact-reactivate-${ct.contact_id}`} onClick={() => onReactivate(ct)} title="Reactivate" className="p-1.5 rounded hover:bg-white/10 text-zinc-400 hover:text-emerald-400"><ArrowCounterClockwise size={14} /></button>
          : <button data-testid={`contact-deactivate-${ct.contact_id}`} onClick={() => onDeactivate(ct)} title="Remove" className="p-1.5 rounded hover:bg-white/10 text-zinc-400 hover:text-red-400"><UserMinus size={14} /></button>}
      </div>
    </div>
  );
};

export const ContactsSection = ({ customer, orgId, onChanged }) => {
  const [dlg, setDlg] = useState(null); // null | "new" | contact
  const [confirm, setConfirm] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [saving, setSaving] = useState(false);
  const all = customer.contacts || [];
  const active = all.filter((c) => c.active !== false).sort((a, b) => (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0));
  const inactive = all.filter((c) => c.active === false);
  const base = `/support/customers/${customer.customer_id}/contacts`;

  const run = async (fn, msg) => {
    setSaving(true);
    try { await fn(); toast.success(msg); onChanged(); return true; }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save contact"); return false; }
    finally { setSaving(false); }
  };
  const save = async (f) => {
    const body = { org_id: orgId, name: f.name, title: f.title, email: f.email, phone: f.phone, notes: f.notes, is_primary: !!f.is_primary };
    const ok = await run(() => (dlg === "new" ? api.post(base, body) : api.patch(`${base}/${dlg.contact_id}`, body)), dlg === "new" ? "Contact added" : "Contact updated");
    if (ok) setDlg(null);
  };
  const setActive = (ct, val) => run(() => api.patch(`${base}/${ct.contact_id}`, { org_id: orgId, active: val }), val ? "Contact reactivated" : "Contact removed");

  return (
    <div className="rounded-xl border border-white/10 bg-zinc-900/60 p-4" data-testid="contacts-section">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-xs uppercase tracking-wider text-zinc-400">Contacts</h3>
        <Button data-testid="add-contact-button" size="sm" onClick={() => setDlg("new")} className="h-7 px-2.5 text-xs bg-blue-600 hover:bg-blue-500"><Plus size={13} weight="bold" className="mr-1" /> Add contact</Button>
      </div>
      <div className="divide-y divide-white/5">
        {active.map((ct) => <ContactRow key={ct.contact_id} ct={ct} onEdit={setDlg} onDeactivate={setConfirm} />)}
        {active.length === 0 && <div className="py-3 text-sm text-zinc-500">No active contacts</div>}
        {showInactive && inactive.map((ct) => <ContactRow key={ct.contact_id} ct={ct} onEdit={setDlg} onReactivate={(c) => setActive(c, true)} />)}
      </div>
      {inactive.length > 0 && (
        <button data-testid="toggle-inactive-contacts" onClick={() => setShowInactive((v) => !v)} className="mt-2 text-xs text-zinc-500 hover:text-zinc-300">
          {showInactive ? "Hide" : "Show"} removed contacts ({inactive.length})
        </button>
      )}

      <ContactDialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)} contact={dlg === "new" ? null : dlg} onSave={save} saving={saving} />

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="bg-zinc-900 border-white/10" data-testid="contact-remove-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {confirm?.name}?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">The contact is deactivated, not deleted. Existing tickets and time entries keep their history, and you can reactivate the contact later.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/15">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="contact-remove-confirm-button" onClick={() => { setActive(confirm, false); setConfirm(null); }} className="bg-red-600 hover:bg-red-500">Remove contact</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
